import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  ArrowUp,
  Save,
} from "lucide-react";
import { message, Select, Switch, Input, InputNumber, Button } from "antd";
import { useRoundNotificationStore, EscalationLevel, EscalationRule } from "../../../store/RoundNotificationStore";
import { PageLoader } from "../../../components/loader/Loader";

const { TextArea } = Input;

interface FormData {
  name: string;
  description: string;
  roundType: EscalationRule["roundType"];
  triggerEvent: EscalationRule["triggerEvent"];
  escalationLevels: EscalationLevel[];
  autoEscalationEnabled: boolean;
  isActive: boolean;
}

interface EscalationRuleFormProps {
  ruleId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EscalationRuleForm: React.FC<EscalationRuleFormProps> = ({ ruleId: propRuleId, onSuccess, onCancel }) => {
  // Use Zustand store with selectors for better performance
  const escalationRules = useRoundNotificationStore((state) => state.escalationRules);
  const defaults = useRoundNotificationStore((state) => state.defaults);
  const fetchDefaults = useRoundNotificationStore((state) => state.fetchDefaults);
  const fetchEscalationRules = useRoundNotificationStore((state) => state.fetchEscalationRules);
  const createEscalationRule = useRoundNotificationStore((state) => state.createEscalationRule);
  const updateEscalationRule = useRoundNotificationStore((state) => state.updateEscalationRule);
    const isEditing = Boolean(propRuleId);

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<FormData>({
      name: "",
      description: "",
      roundType: "All",
      triggerEvent: "critical_issue",
      escalationLevels: [
        {
          level: 1,
          roleName: "Inspector",
          timeMinutes: 0,
          channels: ["in-app", "email"],
        },
      ],
      autoEscalationEnabled: true,
      isActive: true,
    });

    useEffect(() => {
      fetchDefaults();

      if (isEditing && propRuleId) {
        // Load existing rule
        const loadRule = async () => {
          setLoading(true);
          await fetchEscalationRules();
        };
        loadRule();
      } else {
        // Reset form for create mode
        setFormData({
          name: "",
          description: "",
          roundType: "All",
          triggerEvent: "critical_issue",
          escalationLevels: [
            {
              level: 1,
              roleName: "Inspector",
              timeMinutes: 0,
              channels: ["in-app", "email"],
            },
          ],
          autoEscalationEnabled: true,
          isActive: true,
        });
      }
    }, [propRuleId, isEditing, fetchDefaults, fetchEscalationRules]);

    // Update form when rules are loaded (for edit mode)
    useEffect(() => {
      if (isEditing && propRuleId && escalationRules.length > 0) {
        const rule = escalationRules.find((r) => r._id === propRuleId);
        if (rule) {
          setFormData({
            name: rule.name,
            description: rule.description || "",
            roundType: rule.roundType,
            triggerEvent: rule.triggerEvent,
            escalationLevels: [...rule.escalationLevels],
            autoEscalationEnabled: rule.autoEscalationEnabled,
            isActive: rule.isActive,
          });
        }
        setLoading(false);
      }
    }, [isEditing, propRuleId, escalationRules]);

    const handleAddLevel = () => {
      const newLevel = formData.escalationLevels.length + 1;
      setFormData({
        ...formData,
        escalationLevels: [
          ...formData.escalationLevels,
          {
            level: newLevel,
            roleName: "",
            timeMinutes: newLevel === 1 ? 0 : (newLevel - 1) * 60,
            channels: ["in-app", "email"],
          },
        ],
      });
    };

    const handleRemoveLevel = (index: number) => {
      if (formData.escalationLevels.length <= 1) {
        message.warning("At least one escalation level is required");
        return;
      }
      const updated = formData.escalationLevels.filter((_, i) => i !== index);
      // Re-number levels
      updated.forEach((lvl, i) => {
        lvl.level = i + 1;
      });
      setFormData({ ...formData, escalationLevels: updated });
    };

    const handleLevelChange = (
      index: number,
      field: keyof EscalationLevel,
      value: unknown,
    ) => {
      const updated = [...formData.escalationLevels];
      (updated[index] as any)[field] = value;
      setFormData({ ...formData, escalationLevels: updated });
    };

    const handleSubmit = async () => {
      // Validation
      if (!formData.name.trim()) {
        message.error("Rule name is required");
        return;
      }
      if (!formData.triggerEvent) {
        message.error("Trigger event is required");
        return;
      }
      if (formData.escalationLevels.some((lvl) => !lvl.roleName.trim())) {
        message.error("All escalation levels must have a role name");
        return;
      }

      setLoading(true);
      try {
        if (isEditing && propRuleId) {
          const result = await updateEscalationRule(propRuleId, formData);
          if (result) {
            message.success("Rule updated successfully");
            if (onSuccess) onSuccess();
          } else {
            message.error("Failed to update rule");
          }
        } else {
          const result = await createEscalationRule(formData);
          if (result) {
            message.success("Rule created successfully");
            if (onSuccess) onSuccess();
          } else {
            message.error("Failed to create rule");
          }
        }
      } finally {
        setLoading(false);
      }
    };

    const useDefaultLevels = () => {
      if (defaults?.defaultLevels) {
        setFormData({
          ...formData,
          escalationLevels: [...defaults.defaultLevels],
        });
        message.success("Applied default escalation levels");
      }
    };

    if (loading && isEditing) {
      return (
        <div className="flex justify-center items-center h-64">
          <PageLoader />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Form */}
        <div className="overflow-hidden">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Critical Issue Escalation"
                  size="large"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trigger Event <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.triggerEvent}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      triggerEvent: val as EscalationRule["triggerEvent"],
                    })
                  }
                  style={{ width: "100%" }}
                  size="large"
                  options={[
                    { value: "critical_issue", label: "Critical Issue" },
                    { value: "missed_round", label: "Missed Round" },
                    { value: "sla_breach", label: "SLA Breach" },
                    {
                      value: "repeat_noncompliance",
                      label: "Repeat Non-Compliance",
                    },
                  ]}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Round Type
                </label>
                <Select
                  value={formData.roundType}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      roundType: val as EscalationRule["roundType"],
                    })
                  }
                  style={{ width: "100%" }}
                  size="large"
                  options={
                    defaults?.roundTypes.map((t) => ({
                      value: t,
                      label: t,
                    })) || [
                      { value: "All", label: "All" },
                      { value: "Safety", label: "Safety" },
                      { value: "Facility", label: "Facility" },
                    ]
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Optional description"
                  size="large"
                />
              </div>
            </div>

            {/* Escalation Levels */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">
                  Escalation Levels
                </label>
                <div className="flex gap-2">
                  {defaults?.defaultLevels && (
                    <Button size="small" onClick={useDefaultLevels}>
                      Use Defaults
                    </Button>
                  )}
                  <Button
                    type="primary"
                    size="small"
                    icon={<Plus size={14} />}
                    onClick={handleAddLevel}
                    className="bg-[#0fa9a6]"
                  >
                    Add Level
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {formData.escalationLevels.map((level, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-2 w-16">
                      <ArrowUp size={16} className="text-purple-500" />
                      <span className="font-semibold text-purple-700">
                        L{level.level}
                      </span>
                    </div>

                    <div className="flex-1 min-w-[200px]">
                      <Input
                        value={level.roleName}
                        onChange={(e) =>
                          handleLevelChange(index, "roleName", e.target.value)
                        }
                        placeholder="Role name"
                        size="middle"
                      />
                    </div>

                    <div className="w-28">
                      <InputNumber
                        value={level.timeMinutes}
                        onChange={(val) =>
                          handleLevelChange(index, "timeMinutes", val || 0)
                        }
                        min={0}
                        addonAfter="min"
                        size="middle"
                        className="w-full"
                      />
                    </div>

                    <div className="w-40">
                      <Select
                        mode="multiple"
                        value={level.channels}
                        onChange={(val) =>
                          handleLevelChange(index, "channels", val)
                        }
                        style={{ width: "100%" }}
                        size="middle"
                        maxTagCount={1}
                        options={
                          defaults?.channels.map((c) => ({
                            value: c,
                            label: c,
                          })) || [
                            { value: "in-app", label: "in-app" },
                            { value: "email", label: "email" },
                          ]
                        }
                      />
                    </div>

                    <button
                      onClick={() => handleRemoveLevel(index)}
                      className="p-2 hover:bg-red-100 rounded-full text-red-500 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Switches */}
            <div className="flex items-center gap-8 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.autoEscalationEnabled}
                  onChange={(checked) =>
                    setFormData({ ...formData, autoEscalationEnabled: checked })
                  }
                />
                <span className="text-sm text-gray-700">
                  Auto-escalation enabled
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.isActive}
                  onChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
                <span className="text-sm text-gray-700">Rule is active</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3">
            <Button size="large" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={loading}
              className="bg-[#0fa9a6]"
            >
              {isEditing ? "Update Rule" : "Create Rule"}
            </Button>
          </div>
        </div>
      </div>
    );
};

export default EscalationRuleForm;
